import React from "react";
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { Loader2, Mail, Sparkles } from 'lucide-react';

/* ────────────────────────────────────────────────────
   Auth Page (Lavender Dream)
   ──────────────────────────────────────────────────── */

const GRADIENT_BG = `
  radial-gradient(ellipse 90% 70% at 5% -5%, rgba(175, 158, 252, 0.6) 0%, transparent 55%),
  radial-gradient(ellipse 70% 60% at 95% 8%, rgba(218, 148, 238, 0.45) 0%, transparent 52%),
  radial-gradient(ellipse 55% 45% at 50% 105%, rgba(155, 195, 245, 0.35) 0%, transparent 55%),
  radial-gradient(ellipse 45% 35% at 85% 55%, rgba(245, 158, 182, 0.28) 0%, transparent 48%),
  #ede9f5
`;

export default function Auth() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [magicLinkSent, setMagicLinkSent] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  const searchParams = new URLSearchParams(window.location.search);
  const nextUrl = searchParams.get('next') || '/ideas';

  useEffect(() => {
    if (user) {
      navigate(nextUrl);
    }
  }, [user, navigate, nextUrl]);

  const handleMagicLink = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const { error } = await supabase.auth.signInWithOtp({
        email,
        options: {
          emailRedirectTo: `${window.location.origin}${nextUrl}`,
        },
      });

      if (error) throw error;

      setMagicLinkSent(true);
      toast({
        title: 'Check your email!',
        description: 'We sent you a magic link to sign in.',
      });
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to send magic link',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    try {
      console.log('[Auth] Starting Google OAuth...');
      const redirectUrl = `${window.location.origin}/auth/callback?next=${encodeURIComponent(nextUrl)}`;
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: redirectUrl,
          skipBrowserRedirect: true,
        },
      });

      if (error) throw error;

      if (data?.url) {
        console.log('[Auth] Redirecting browser to OAuth URL (top window):', data.url);
        try {
          if (window.top && window.top !== window) {
            window.top.location.href = data.url;
            return;
          }
        } catch (err) {
          console.warn('[Auth] Top window redirect blocked, opening new tab instead:', err);
          const popup = window.open(data.url, '_blank', 'noopener,noreferrer');
          if (!popup) {
            console.warn('[Auth] Popup blocked, falling back to same-window redirect');
            window.location.assign(data.url);
          }
          return;
        }
        window.location.assign(data.url);
      } else {
        throw new Error('No OAuth URL returned from provider.');
      }
    } catch (error: any) {
      console.error('[Auth] Google OAuth error:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to sign in with Google',
        variant: 'destructive',
      });
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ background: GRADIENT_BG }}>
      <div
        className="w-full max-w-md rounded-[24px] p-8"
        style={{
          background: "rgba(255, 255, 255, 0.72)",
          backdropFilter: "blur(12px)",
          border: "1px solid rgba(255, 255, 255, 0.85)",
          boxShadow: "0 8px 32px rgba(124, 106, 240, 0.1)",
        }}
      >
        <div className="text-center mb-6">
          <div
            className="h-12 w-12 rounded-full flex items-center justify-center mx-auto mb-4"
            style={{ background: "linear-gradient(135deg, #a898f8, #cc82e8)" }}
          >
            <Sparkles className="h-6 w-6 text-white" />
          </div>
          <h1
            className="text-[24px] text-[#18151f] mb-1"
            style={{ fontFamily: "'Instrument Serif', serif" }}
          >
            Sign in to DayOne
          </h1>
          <p
            className="text-[14px] text-[#6b5f8a]"
            style={{ fontFamily: "'DM Sans', sans-serif" }}
          >
            Sign in or create an account to start building
          </p>
        </div>

        {magicLinkSent ? (
          <div className="text-center py-4">
            <div
              className="h-16 w-16 rounded-full flex items-center justify-center mx-auto mb-4"
              style={{ background: "rgba(124, 106, 240, 0.1)" }}
            >
              <Mail className="h-8 w-8 text-[#7c6af0]" />
            </div>
            <h3
              className="font-semibold text-[#18151f] mb-2"
              style={{ fontFamily: "'DM Sans', sans-serif" }}
            >
              Check your email
            </h3>
            <p className="text-[14px] text-[#6b5f8a] mb-2">
              We sent a magic link to <strong>{email}</strong>
            </p>
            <p className="text-[14px] text-[#6b5f8a] mb-4">
              Click the link in the email to sign in.
            </p>
            <button
              onClick={() => setMagicLinkSent(false)}
              className="text-[14px] text-[#7c6af0] hover:underline"
              style={{ fontFamily: "'DM Sans', sans-serif" }}
            >
              Try a different email
            </button>
          </div>
        ) : (
          <>
            <form onSubmit={handleMagicLink} className="space-y-4">
              <div>
                <label
                  htmlFor="email"
                  className="block text-[13px] font-medium text-[#18151f] mb-2"
                  style={{ fontFamily: "'DM Sans', sans-serif" }}
                >
                  Email
                </label>
                <input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  disabled={loading}
                  className="w-full rounded-xl px-4 py-3 text-[14px] text-[#18151f] placeholder:text-[#6b5f8a]/50 focus:outline-none disabled:opacity-50"
                  style={{
                    fontFamily: "'DM Sans', sans-serif",
                    background: "rgba(255, 255, 255, 0.8)",
                    border: "1px solid rgba(124, 106, 240, 0.3)",
                  }}
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                className="w-full flex items-center justify-center gap-2 px-6 py-3 text-[14px] font-medium text-white rounded-full transition-all disabled:opacity-50"
                style={{
                  background: "#18151f",
                  fontFamily: "'DM Sans', sans-serif",
                }}
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Sending magic link...
                  </>
                ) : (
                  <>
                    <Mail className="h-4 w-4" />
                    Continue with email
                  </>
                )}
              </button>
            </form>

            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full" style={{ borderTop: "1px solid rgba(124, 106, 240, 0.2)" }} />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span
                  className="px-2 text-[#6b5f8a]"
                  style={{
                    background: "rgba(255, 255, 255, 0.72)",
                    fontFamily: "'DM Sans', sans-serif",
                  }}
                >
                  Or continue with
                </span>
              </div>
            </div>

            <button
              onClick={handleGoogleSignIn}
              type="button"
              className="w-full flex items-center justify-center gap-2 px-6 py-3 text-[14px] font-medium text-[#18151f] rounded-full transition-all hover:bg-white/80"
              style={{
                background: "rgba(255, 255, 255, 0.8)",
                border: "1px solid rgba(124, 106, 240, 0.3)",
                fontFamily: "'DM Sans', sans-serif",
              }}
            >
              <svg className="h-4 w-4" viewBox="0 0 24 24">
                <path
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  fill="#4285F4"
                />
                <path
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  fill="#34A853"
                />
                <path
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  fill="#FBBC05"
                />
                <path
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  fill="#EA4335"
                />
              </svg>
              Continue with Google
            </button>
          </>
        )}
      </div>
    </div>
  );
}
